import { obtenerMensajeError } from "utils/apiErrors";
import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Container,
  Typography,
  Paper,
  Menu,
  MenuItem,
  Box,
  Backdrop,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Select,
  Checkbox,
  InputLabel,
  Divider,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { verificarEtapaAnterior } from "../utils/etapaHelpers";

import useUserRoles from "hooks/useUserRoles";
import useAudienciaEditorImages from "./hooks/useAudienciaEditorImages";

import ProcesosApi from "api/procesosApi";

import ReactQuill from "react-quill";
import styled from "styled-components";
import "react-quill/dist/quill.snow.css";

const StyledEditor = styled(ReactQuill)`
  .ql-toolbar {
    background-color: #f5f5f5;
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    min-height: 200px;
    font-size: 16px;
    border-radius: 0 0 8px 8px;
    font-family: "Roboto", sans-serif;
  }
  .ql-editor {
    padding: 16px;
    line-height: 1.5;
  }
  .ql-toolbar button:hover {
    background-color: #e0e0e0;
  }
  .ql-editor img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 10px auto;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ContainerObjeciones = ({ proceso, audiencia: initialAudiencia, onAudienciaChange }) => {
  const { user, roles } = useUserRoles();
  const [consideraciones, setConsideraciones] = useState("");
  const [resolucion, setResolucion] = useState("");
  const quillConsideracionesRef = useRef(null);
  const quillResolucionRef = useRef(null);
  const [checkedCumplida, setCheckedCumplida] = useState(false);
  const [objeciones, setObjeciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acreedores, setAcreedores] = useState([]);
  const [apoderados, setApoderados] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedObjecion, setSelectedObjecion] = useState(null);
  const [tipoObjetante, setTipoObjetante] = useState("");
  const [audiencia, setAudiencia] = useState(initialAudiencia);
  const [ordenActual] = useState(4);
  const [verificacion, setVerificacion] = useState(null);
  const [openNewDialog, setOpenNewDialog] = useState(false);
  const [newObjecion, setNewObjecion] = useState({
    descripcion: "",
    observacion: "",
  });
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hook personalizado para manejo de imágenes específico de audiencias
  const {
    uploadingImage,
    imageError,
    setupImageHandler,
    processImagesForPDF,
    getQuillModules,
    getQuillFormats,
    validateContent,
    cleanOrphanImages,
    clearError,
    resetImageCounters,
  } = useAudienciaEditorImages(proceso?.identificador, initialAudiencia?.id, "objeciones");

  useEffect(() => {
    setAudiencia(initialAudiencia);
    fetchDatosIniciales();
    const etapaActual = initialAudiencia?.etapas?.find((etapa) => etapa.orden === ordenActual);
    if (etapaActual?.resultado?.consideraciones) {
      setConsideraciones(etapaActual.resultado.consideraciones);
    }
    if (etapaActual?.resultado?.resolucion) {
      setResolucion(etapaActual.resultado.resolucion);
    }
    if (etapaActual?.estado === "CUMPLIDA" || etapaActual?.estado === "CERRADA") {
      setCheckedCumplida(true);
    } else if (etapaActual?.estado === "PENDIENTE" || etapaActual?.estado === "PROGRAMADA") {
      setCheckedCumplida(false);
    }
    // Solo verificar etapas si existe audiencia y sus etapas
    if (initialAudiencia && initialAudiencia.etapas) {
      setVerificacion(verificarEtapaAnterior(initialAudiencia.etapas, ordenActual));
    }
    // Resetear contadores de imágenes cuando cambie la audiencia
    resetImageCounters();
  }, [user, proceso, initialAudiencia, resetImageCounters]);

  // Configurar manejadores de imagen cuando los refs estén listos
  useEffect(() => {
    const timer = setTimeout(() => {
      setupImageHandler(quillConsideracionesRef, "consideraciones", (imageInfo) => {
        console.log("Imagen subida en consideraciones:", imageInfo);
      });
      setupImageHandler(quillResolucionRef, "resolucion", (imageInfo) => {
        console.log("Imagen subida en resolución:", imageInfo);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [consideraciones, resolucion, setupImageHandler]);

  const fetchDatosIniciales = async () => {
    try {
      setLoading(true);
      const [apoderadosRes, objecionesRes] = await Promise.all([
        ProcesosApi.fetchParticipantesAudiencia(audiencia.id),
        ProcesosApi.obtenerObjecionesProceso(proceso.id),
      ]);
      const acreedoresCombinados = [...(apoderadosRes.data.acreedores ?? [])];
      setAcreedores(acreedoresCombinados);
      const apoderadosCombinados = [];
      if (apoderadosRes.data.deudor_apoderado) {
        apoderadosCombinados.push(apoderadosRes.data.deudor_apoderado);
      }
      if (apoderadosRes.data.acreedores) {
        apoderadosRes.data.acreedores.forEach((acreedor) => {
          if (acreedor.apoderado) {
            apoderadosCombinados.push(acreedor.apoderado);
          }
        });
      }
      setApoderados(apoderadosCombinados);
      setObjeciones(objecionesRes.data);
    } catch (error) {
      console.error("Error obteniendo los datos iniciales:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let objecion = objeciones.find((a) => a.id === id);
    setSelectedObjecion(objecion);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleOpenNewDialog = () => {
    setOpenNewDialog(true);
  };

  const handleCloseNewDialog = () => {
    setOpenNewDialog(false);
    setNewObjecion({ objeto: "", descripcion: "" });
  };

  const handleTipoObjetanteChange = (event) => {
    setTipoObjetante(event.target.value);
    setNewObjecion({
      ...newObjecion,
      objetante: {},
    });
  };

  const handleCreateObjecion = async () => {
    if (!newObjecion.objetante) {
      alert("Debe seleccionar un Objetante.");
      return;
    }
    if (!newObjecion.tipo_objecion) {
      alert("Debe seleccionar un Tipo de Objeción.");
      return;
    }
    try {
      setCreating(true);
      const nuevaObjecion = {
        proceso: proceso.id,
        audiencia: audiencia.id,
        objetante:
          tipoObjetante == "DEUDOR"
            ? {
                tipo: "DEUDOR",
                id: proceso.cliente_detalle.id,
                datos: proceso.cliente_detalle,
              }
            : newObjecion.objetante,
        tipo: "Objecion",
        tipo_objecion: newObjecion.tipo_objecion,
        descripcion: newObjecion.descripcion,
      };
      await ProcesosApi.crearObjecion(nuevaObjecion);
      handleCloseNewDialog();
      fetchDatosIniciales();
    } catch (error) {
      console.error("Error creando la objeción:", error);
    } finally {
      setCreating(false);
    }
  };

  const handleResolver = async () => {
    const confirmAction = window.confirm(`¿Estás seguro(a) que deseas resolver esta Objeción?`);
    if (confirmAction) {
      const datos = {
        operacion: "RESOLVER",
        id_objecion: selectedId,
      };
      try {
        setSaving(true);
        const response = await ProcesosApi.operarObjecion(datos);
        if (response.status === 201 && response.data) {
          alert("La Objeción ha sido resuelta.");
        }
        fetchDatosIniciales();
      } catch (error) {
        alert(obtenerMensajeError(error, "Error al aceptar el proceso"));
      } finally {
        setSaving(false);
      }
    }
    handleMenuClose();
  };

  const handleDevolver = async () => {
    const confirmAction = window.confirm(`¿Estás seguro(a) que deseas devolver esta Objeción?`);
    if (confirmAction) {
      const datos = {
        operacion: "DEVOLVER",
        id_objecion: selectedId,
      };
      try {
        setSaving(true);
        const response = await ProcesosApi.operarObjecion(datos);
        if (response.status === 201 && response.data) {
          alert("La Objeción ha sido devuelta.");
        }
        fetchDatosIniciales();
      } catch (error) {
        alert(obtenerMensajeError(error, "Error al aceptar el proceso"));
      } finally {
        setSaving(false);
      }
    }
    handleMenuClose();
  };

  const handleSave = async () => {
    // Limpiar errores de imagen
    clearError();
    const objecionesSeguros = Array.isArray(objeciones) ? objeciones : [];
    const hayObjecionesNoConciliadas = objecionesSeguros.some(
      (objecion) => objecion.estado === "Presentada"
    );
    let nuevoEstado;
    let mensConfirm = "";
    if (hayObjecionesNoConciliadas) {
      // Validar contenido
      const consideracionesValidation = validateContent(consideraciones);
      const resolucionValidation = validateContent(resolucion);
      if (!consideracionesValidation.isValid) {
        alert("Error en consideraciones: " + consideracionesValidation.errors.join(", "));
        return;
      }
      if (!resolucionValidation.isValid) {
        alert("Error en resolución: " + resolucionValidation.errors.join(", "));
        return;
      }
      if (checkedCumplida) {
        nuevoEstado = "CERRADA";
        mensConfirm = `La etapa de Objeciones quedará ${nuevoEstado} generando una Prorroga ya que hay Objeciones en estado Presentada.`;
      } else {
        nuevoEstado = "PENDIENTE";
        mensConfirm = `La etapa de Objeciones quedará ${nuevoEstado}, con objeciones en estado Presentada.`;
      }
    } else {
      nuevoEstado = checkedCumplida ? "CUMPLIDA" : "PENDIENTE";
      mensConfirm = `La etapa de Objeciones quedará ${nuevoEstado}, sin objeciones en estado Presentada.`;
    }
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas guardar la etapa de Objeciones? ${mensConfirm}`
    );
    if (confirmAction) {
      try {
        setSaving(true);

        // Procesar imágenes para PDF
        const htmlConsideracionesProcesadas = await processImagesForPDF(consideraciones);
        const htmlResolucionProcesado = await processImagesForPDF(resolucion);

        await Promise.all(
          objecionesSeguros.map(async (objecion) => {
            await ProcesosApi.actualizarObjecion(objecion.id, objecion);
          })
        );
        const objecionesResultado = objecionesSeguros.map((objecion) => ({
          id_objecion: objecion.id,
          objetante: objecion.objetante,
          tipo: objecion.tipo,
          tipo_objecion: objecion.tipo_objecion,
          descripcion: objecion.descripcion,
          estado: objecion.estado,
        }));

        const parser = new DOMParser();
        const docConsideraciones = parser.parseFromString(
          htmlConsideracionesProcesadas,
          "text/html"
        );
        const htmlLimpioConsideraciones = docConsideraciones.body.innerHTML;
        const docResolucion = parser.parseFromString(htmlResolucionProcesado, "text/html");
        const htmlLimpioResolucion = docResolucion.body.innerHTML;

        const updatedAudiencia = {
          ...audiencia,
          estado: "REALIZADA",
          etapas: audiencia.etapas.map((etapa) =>
            etapa.orden === ordenActual
              ? {
                  ...etapa,
                  estado: nuevoEstado,
                  resultado: {
                    objeciones: objecionesResultado,
                    consideraciones: htmlLimpioConsideraciones,
                    resolucion: htmlLimpioResolucion,
                  },
                }
              : etapa
          ),
        };
        await ProcesosApi.actualizarAudiencia(audiencia.id, updatedAudiencia);

        // Limpiar imágenes huérfanas después de guardar
        console.log("Iniciando limpieza de imágenes huérfanas después de guardar...");
        try {
          const cleanResult = await cleanOrphanImages({
            consideraciones: consideraciones,
            resolucion: resolucion,
          });
          if (cleanResult.success) {
            console.log(
              `Limpieza completada: ${cleanResult.deleted_count || 0} archivos eliminados`
            );
            if (cleanResult.deleted_files && cleanResult.deleted_files.length > 0) {
              console.log("Archivos eliminados:", cleanResult.deleted_files);
            }
          } else {
            console.warn("Error en limpieza de imágenes:", cleanResult.error);
          }
        } catch (cleanError) {
          console.error("Error ejecutando limpieza de imágenes:", cleanError);
        }

        console.log("Audiencia actualizada correctamente");
        setAudiencia(updatedAudiencia);
        onAudienciaChange && onAudienciaChange(updatedAudiencia);
        fetchDatosIniciales();
        alert("Etapa guardada exitosamente");
      } catch (error) {
        console.error("Error al guardar la Etapa", error);
        alert(
          obtenerMensajeError(error, "Error al guardar la etapa. Por favor intenta nuevamente.")
        );
      } finally {
        setSaving(false);
      }
    }
  };

  const handleAbrirEtapa = async () => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas abrir la Etapa de Objeciones?`
    );
    if (confirmAction) {
      try {
        setSaving(true);
        const updatedAudiencia = {
          ...audiencia,
          etapas: audiencia.etapas.map((etapa) => {
            if (etapa.orden === ordenActual) {
              return {
                ...etapa,
                estado: "PENDIENTE",
              };
            } else if (etapa.orden > ordenActual) {
              return {
                ...etapa,
                estado: "PROGRAMADA",
              };
            }
            return etapa;
          }),
        };
        await ProcesosApi.actualizarAudiencia(audiencia.id, updatedAudiencia);
        setAudiencia(updatedAudiencia);
        onAudienciaChange && onAudienciaChange(updatedAudiencia);
        fetchDatosIniciales();
      } catch (error) {
        console.error("Error al abrir la Etapa", error);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleEditorConsideracionesChange = useCallback(
    (value) => {
      setConsideraciones(value);
      clearError(); // Limpiar errores cuando el usuario escriba
    },
    [clearError]
  );

  const handleEditorResolucionChange = useCallback(
    (value) => {
      setResolucion(value);
      clearError(); // Limpiar errores cuando el usuario escriba
    },
    [clearError]
  );

  const handleDateChange = (newValue) => {
    if (newValue) {
      const year = newValue.$y;
      const month = String(newValue.$M + 1).padStart(2, "0");
      const day = String(newValue.$D).padStart(2, "0");
      const fecha = `${year}-${month}-${day}`;
      setNewObjecion({
        ...newObjecion,
        fecha_objecion: fecha,
      });
    }
  };

  const columns = [
    {
      field: "Opciones",
      renderHeader: () => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <AddCircleIcon
            onClick={!isDisabled() ? handleOpenNewDialog : undefined}
            fontSize="large"
            sx={{
              cursor: isDisabled() ? "not-allowed" : "pointer",
              color: isDisabled() ? "gray" : "inherit",
              opacity: isDisabled() ? 0.5 : 1,
            }}
          />
        </Box>
      ),
      width: 100,
      sortable: false,
      align: "center",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)} sx={{ padding: "2px" }}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "objetante",
      headerName: "Objetante",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.objetante?.tipo || "No tiene."}
          {": "}
          {params.row.objetante?.datos.nombre_completo || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "tipo",
      headerName: "Tipo",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.tipo + " - " + params.row.tipo_objecion || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.descripcion || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "estado",
      headerName: "Estado",
      flex: 1,
      renderCell: (params) => {
        const objecion = params.row;
        let estado = objecion.estado || "Presentada";
        const estadoStyles = {
          Presentada: { backgroundColor: "#FF5733", color: "white" },
          Resuelta: { backgroundColor: "#43A047", color: "white" },
        };
        const style = estadoStyles[estado] || { backgroundColor: "gray", color: "white" };
        return (
          <Tooltip title={estado}>
            <div
              style={{
                display: "inline-block",
                backgroundColor: style.backgroundColor,
                color: style.color,
                padding: "4px 12px",
                borderRadius: "12px",
                fontWeight: "bold",
                fontSize: "0.9rem",
                textAlign: "center",
                minWidth: "80px",
              }}
            >
              {estado}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const isDisabled = () => {
    return (
      !verificacion ||
      audiencia.estado === "SUSPENDIDA" ||
      audiencia.estado === "CERRADA" ||
      (ordenActual > 1 && !verificacion.isQuorumCumplido) ||
      (ordenActual > 1 && !verificacion.isEtapaAnteriorCumplida) ||
      //(!verificacion.isEtapaActualPendiente &&
      //  !verificacion.isEtapaActualCerrada &&
      //  !verificacion.isEtapaActualProgramada) ||
      verificacion.estadoActual === "CUMPLIDA" ||
      verificacion.estadoActual === "CERRADA" ||
      (ordenActual === 1 && verificacion.estadoActual !== "CUMPLIDA") ||
      creating ||
      saving ||
      uploadingImage
    );
  };

  const isOpenEtapaDisabled = () => {
    return (
      !verificacion ||
      initialAudiencia.estado === "SUSPENDIDA" ||
      initialAudiencia.estado === "CERRADA" ||
      (ordenActual > 1 && !verificacion.isQuorumCumplido) ||
      (ordenActual > 1 && !verificacion.isEtapaAnteriorCumplida) ||
      verificacion.isEtapaActualPendiente ||
      verificacion.isEtapaActualProgramada ||
      verificacion.isEtapaSiguienteCumplida ||
      creating ||
      saving ||
      uploadingImage
    );
  };

  return (
    <Container>
      {(loading || saving || uploadingImage) && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2, color: "white" }}>
              {uploadingImage ? "Subiendo imagen..." : saving ? "Guardando..." : "Cargando..."}
            </Typography>
          </Box>
        </Backdrop>
      )}

      {imageError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {imageError}
        </Alert>
      )}

      {proceso && audiencia ? (
        <>
          <SuiBox>
            <Paper
              sx={{
                width: "100%",
                overflow: "hidden",
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            >
              <DataGrid
                rows={objeciones}
                columns={columns}
                loading={loading}
                getRowHeight={() => "auto"}
                sx={{
                  border: 0,
                  backgroundColor: "white",
                  borderRadius: 2,
                  p: 0.5,
                  [`& .${gridClasses.cell}`]: {
                    borderBottom: "none",
                    padding: "2px 2px",
                  },
                  [`& .${gridClasses.row}`]: {
                    backgroundColor: ({ palette: { white } }) => white.main,
                    "&:hover": {
                      backgroundColor: ({ palette: { grey } }) => grey[300],
                    },
                    minHeight: "24px !important",
                  },
                  [`& .${gridClasses.columnHeaders}`]: {
                    backgroundColor: ({ palette: { grey } }) => grey[50],
                    borderRadius: 1,
                    minHeight: "12px !important",
                  },
                  [`& .${gridClasses.footerContainer}`]: {
                    display: "none",
                  },
                }}
                pageSize={10}
                disableSelectionOnClick
                disableColumnMenu
                hideFooter={objeciones.length <= 10}
                components={{
                  NoRowsOverlay: () => (
                    <Box
                      py={6}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <SuiTypography variant="body2" color="textSecondary">
                        No hay objeciones registradas para este proceso.
                      </SuiTypography>
                    </Box>
                  ),
                  LoadingOverlay: () => (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress />
                    </Box>
                  ),
                }}
              />
            </Paper>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              {(roles.isAdmin || roles.isOperador) && selectedObjecion?.estado === "Presentada" && (
                <MenuItem onClick={() => handleResolver()} disabled={isDisabled()}>
                  <ThumbUpAltIcon title="Resolver" fontSize="small" sx={{ mr: 1 }} /> Resolver
                </MenuItem>
              )}
              {(roles.isAdmin || roles.isOperador) && selectedObjecion?.estado === "Resuelta" && (
                <MenuItem onClick={() => handleDevolver()} disabled={isDisabled()}>
                  <ArrowBackIosNewIcon title="Devolver" fontSize="small" sx={{ mr: 1 }} /> Devolver
                </MenuItem>
              )}
            </Menu>
          </SuiBox>
          <Divider sx={{ borderColor: "gray", borderStyle: "groove" }} />
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <SuiTypography variant="h6" fontWeight="bold">
                Consideraciones Objeciones
              </SuiTypography>
              {uploadingImage && (
                <Typography variant="caption" color="primary">
                  Subiendo imagen...
                </Typography>
              )}
            </Grid>
            <Grid size={12}>
              <StyledEditor
                ref={quillConsideracionesRef}
                value={consideraciones}
                onChange={handleEditorConsideracionesChange}
                modules={getQuillModules()}
                formats={getQuillFormats()}
                readOnly={isDisabled()}
                placeholder="Ingrese las consideraciones de objeciones. Puede insertar imágenes usando el botón de imagen en la barra de herramientas."
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <SuiTypography variant="h6" fontWeight="bold">
                Resolución Objeciones (Punto 2)
              </SuiTypography>
              {uploadingImage && (
                <Typography variant="caption" color="primary">
                  Subiendo imagen...
                </Typography>
              )}
            </Grid>
            <Grid size={12}>
              <StyledEditor
                ref={quillResolucionRef}
                value={resolucion}
                onChange={handleEditorResolucionChange}
                modules={getQuillModules()}
                formats={getQuillFormats()}
                readOnly={isDisabled()}
                placeholder="Ingrese la resolución de objeciones. Puede insertar imágenes usando el botón de imagen en la barra de herramientas."
              />
            </Grid>
          </Grid>
          {(roles.isAdmin || roles.isOperador) && (
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={8} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <InputLabel sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={checkedCumplida}
                    disabled={isDisabled()}
                    onChange={(e) => setCheckedCumplida(e.target.checked)}
                    size="small"
                    sx={{
                      color: "white",
                      "&.Mui-checked": {
                        color: "white",
                      },
                      padding: "4px",
                    }}
                  />
                  <SuiTypography variant="h6" fontWeight="light">
                    ¿Cumplida?
                  </SuiTypography>
                </InputLabel>
                <SuiButton
                  variant="gradient"
                  buttonColor="info"
                  onClick={handleSave}
                  disabled={isDisabled()}
                >
                  Guardar Etapa
                </SuiButton>
                <SuiTypography variant="caption" fontWeight="bold">
                  {!verificacion
                    ? "Cargando verificación..."
                    : audiencia.estado === "SUSPENDIDA"
                    ? "- Audiencia suspendida (no modificable)"
                    : !verificacion.isQuorumCumplido && ordenActual > 1
                    ? "- No se puede avanzar: Quórum no completado"
                    : !verificacion.isEtapaAnteriorCumplida && ordenActual > 1
                    ? "- Complete la etapa anterior primero"
                    : !verificacion.isEtapaActualPendiente &&
                      !verificacion.isEtapaActualCerrada &&
                      !verificacion.isEtapaActualProgramada
                    ? "- Esta etapa ya fue completada"
                    : ordenActual === 1 && verificacion.estadoActual !== "CUMPLIDA"
                    ? "- Quórum pendiente de completar"
                    : uploadingImage
                    ? "- Subiendo imagen..."
                    : null}
                </SuiTypography>
                <SuiTypography variant="caption" fontWeight="bold">
                  {!verificacion
                    ? "Cargando verificación..."
                    : verificacion.isEtapaActualCerrada
                    ? "- Esta etapa se encuentra cerrada"
                    : null}
                </SuiTypography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  onClick={handleAbrirEtapa}
                  disabled={isOpenEtapaDisabled()}
                >
                  Abrir Etapa
                </SuiButton>
              </Grid>
            </Grid>
          )}

          <Dialog open={openNewDialog} onClose={handleCloseNewDialog} maxWidth="lg" fullWidth>
            <DialogTitle>
              <SuiBox mt={1}>
                <strong>
                  Nueva Objeción al Proceso: {proceso ? proceso.identificador : "No tiene Proceso"}
                </strong>
              </SuiBox>
            </DialogTitle>
            <DialogContent>
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={4}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Tipo Objetante
                  </SuiTypography>
                  <Select value={tipoObjetante} onChange={handleTipoObjetanteChange}>
                    <MenuItem value="">Seleccione Tipo Responsable</MenuItem>
                    <MenuItem value="ACREEDOR">ACREEDOR</MenuItem>
                    <MenuItem value="APODERADO">APODERADO</MenuItem>
                    <MenuItem value="DEUDOR">DEUDOR</MenuItem>
                  </Select>
                </Grid>
                <Grid size={8}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Objetante
                  </SuiTypography>
                  {tipoObjetante === "DEUDOR" && (
                    <SuiInput value={proceso.cliente_detalle.nombre_completo} disabled fullWidth />
                  )}
                  {tipoObjetante === "ACREEDOR" && (
                    <Autocomplete
                      disablePortal
                      options={acreedores}
                      getOptionLabel={(option) => option?.nombre_completo || ""}
                      sx={{ mb: 3 }}
                      renderInput={(params) => <TextField {...params} />}
                      value={newObjecion.objetante?.datos}
                      onChange={(_, newValue) =>
                        setNewObjecion({
                          ...newObjecion,
                          objetante: { tipo: "ACREEDOR", id: newValue.id, datos: newValue },
                        })
                      }
                    />
                  )}
                  {tipoObjetante === "APODERADO" && (
                    <Autocomplete
                      disablePortal
                      options={apoderados}
                      getOptionLabel={(option) => option?.nombre_completo || ""}
                      sx={{ mb: 3 }}
                      renderInput={(params) => <TextField {...params} />}
                      value={newObjecion.objetante?.datos}
                      onChange={(_, newValue) =>
                        setNewObjecion({
                          ...newObjecion,
                          objetante: { tipo: "APODERADO", id: newValue.id, datos: newValue },
                        })
                      }
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={6}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Tipo Objeción
                  </SuiTypography>
                  <Select
                    value={newObjecion.tipo_objecion}
                    onChange={(e) =>
                      setNewObjecion({ ...newObjecion, tipo_objecion: e.target.value })
                    }
                    fullWidth
                  >
                    <MenuItem value="Objeción de Existencia">Objeción de Existencia</MenuItem>
                    <MenuItem value="Objeción de Naturaleza">Objeción de Naturaleza</MenuItem>
                    <MenuItem value="Objeción de Cuantía">Objeción de Cuantía</MenuItem>
                  </Select>
                </Grid>
                <Grid size={6}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Fecha Objeción
                  </SuiTypography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      name="fecha_objecion"
                      value={newObjecion.fecha_objecion ? dayjs(newObjecion.fecha_objecion) : null}
                      onChange={handleDateChange}
                      format="DD/MM/YYYY"
                      renderInput={(params) => (
                        <SuiInput
                          {...params}
                          icon={{
                            component: <CalendarTodayIcon />,
                            direction: "right",
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={6}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Descripción
                  </SuiTypography>
                  <SuiInput
                    name="descripcion"
                    multiline
                    rows={4}
                    value={newObjecion.descripcion}
                    onChange={(e) =>
                      setNewObjecion({ ...newObjecion, descripcion: e.target.value })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Observación
                  </SuiTypography>
                  <SuiInput
                    name="observacion"
                    multiline
                    rows={4}
                    value={newObjecion.observacion}
                    onChange={(e) =>
                      setNewObjecion({ ...newObjecion, observacion: e.target.value })
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <SuiButton onClick={handleCloseNewDialog} color="secondary">
                Cancelar
              </SuiButton>
              <SuiButton
                variant="gradient"
                buttonColor="dark"
                onClick={handleCreateObjecion}
                disabled={creating}
              >
                {creating ? "Guardando..." : "Guardar"}
              </SuiButton>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Typography>Cargando...</Typography>
      )}
    </Container>
  );
};

ContainerObjeciones.propTypes = {
  proceso: PropTypes.object,
  audiencia: PropTypes.object,
  onAudienciaChange: PropTypes.func.isRequired,
};

export default ContainerObjeciones;
